import React from 'react';
import { useField } from 'formik';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { FormTextField } from './FormTextField';

interface Option {
    label: string;
    value: string;
}

interface FormSelectProps {
    name: string;
    label?: string;
    helpText?: string;
    placeholder?: string;
    options: Option[];
    className?: string;
    disabled?: boolean;
    required?: boolean;
    showOtherOption?: boolean;
    validate?: (value: unknown) => string | undefined;
}

export const FormSelect: React.FC<FormSelectProps> = ({
    name,
    label,
    helpText,
    placeholder,
    options,
    className,
    disabled,
    required,
    showOtherOption = false,
    validate,
}) => {
    const validateFn = (value: unknown) => {
        if (required && !value) return 'Campo obrigatório';
        if (validate) return validate(value);
        return undefined;
    };

    const [field, meta, helpers] = useField({ name, validate: validateFn });

    const showOtherField =
        showOtherOption &&
        field.value &&
        (field.value === 'other' ||
            field.value.toLowerCase().includes('outro') ||
            field.value.toLowerCase().includes('outros'));

    return (
        <div className={cn('space-y-2', className)}>
            {label && (
                <div className="space-y-1">
                    <Label className={cn(meta.touched && meta.error && 'text-destructive')}>
                        {label} {required ? '*' : ''}
                    </Label>
                    {helpText ? (
                        <p className="text-sm leading-relaxed text-muted-foreground">{helpText}</p>
                    ) : null}
                </div>
            )}
            <Select value={field.value} onValueChange={helpers.setValue} disabled={disabled}>
                <SelectTrigger
                    className={cn(
                        meta.touched && meta.error && 'border-destructive focus:ring-destructive',
                    )}
                >
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                    {options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {meta.touched && meta.error ? (
                <p className="text-[0.8rem] font-medium text-destructive">{meta.error}</p>
            ) : null}
            {showOtherField && (
                <FormTextField
                    name={`${name}_other_answer`}
                    label="Especifique"
                    required={true}
                    disabled={disabled}
                />
            )}
        </div>
    );
};
